import React, { useEffect, useState } from 'react';
import { Save, Plus, Trash2, ChevronUp, ChevronDown, Star } from 'lucide-react';
import { toast } from 'sonner';
import adminApi from '../api/adminApi';
import BilingualField from '../components/BilingualField';
import ImagePicker from '../components/ImagePicker';

const inputClass = "w-full bg-[#1A1A1A] border border-[rgba(212,175,55,0.2)] rounded-sm px-3 py-2 text-white text-sm placeholder-white/30 focus:outline-none focus:border-[#D4AF37] transition-colors";
const labelClass = "block text-xs uppercase tracking-wider text-[#D4AF37] font-semibold mb-2";
const Card = ({ title, children }) => (
  <div className="bg-[#111111] border border-[rgba(212,175,55,0.15)] rounded-sm p-5 space-y-4">
    <h2 className="text-sm font-semibold text-white border-b border-[rgba(212,175,55,0.1)] pb-3">{title}</h2>
    {children}
  </div>
);

const emptyCategory = () => ({
  id: `cat-${Date.now()}`, key: `category-${Date.now()}`,
  icon: 'crown', title_en: '', title_ar: '', description_en: '', description_ar: '',
  badge_en: '', badge_ar: '',
  tagline_en: '', tagline_ar: '',
  modal_content_en: '', modal_content_ar: '',
  features_en: '', features_ar: '',
  variants_en: '', variants_ar: '',
  origins_en: '', origins_ar: '',
  image_url: '', featured: false, enabled: true, order: 99,
});

const CategoryCard = ({ cat, index, total, onChange, onDelete, onMoveUp, onMoveDown }) => {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="bg-[#1A1A1A] border border-[rgba(212,175,55,0.15)] rounded-sm overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3 cursor-pointer" onClick={() => setExpanded(e => !e)}>
        {cat.image_url && <img src={cat.image_url} alt="" className="w-12 h-9 object-cover rounded-sm flex-shrink-0" />}
        {!cat.image_url && <div className="w-12 h-9 bg-[#0A0A0A] rounded-sm flex-shrink-0" />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <p className="text-white text-sm font-medium truncate">{cat.title_en || '(untitled)'}</p>
            {cat.featured && <Star className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37] flex-shrink-0" />}
          </div>
          <p className="text-white/40 text-xs truncate">{cat.description_en}</p>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={e => { e.stopPropagation(); onMoveUp(); }} disabled={index === 0} className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white disabled:opacity-20 transition-colors"><ChevronUp className="w-3.5 h-3.5" /></button>
          <button onClick={e => { e.stopPropagation(); onMoveDown(); }} disabled={index === total - 1} className="w-7 h-7 flex items-center justify-center text-white/40 hover:text-white disabled:opacity-20 transition-colors"><ChevronDown className="w-3.5 h-3.5" /></button>
          <button onClick={e => { e.stopPropagation(); onDelete(); }} className="w-7 h-7 flex items-center justify-center text-white/30 hover:text-red-400 transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      </div>
      {expanded && (
        <div className="px-4 pb-4 space-y-4 border-t border-[rgba(212,175,55,0.1)] pt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Icon</label>
              <select value={cat.icon} onChange={e => onChange({ ...cat, icon: e.target.value })} className={inputClass}>
                <option value="crown">Crown</option>
                <option value="leaf">Leaf</option>
                <option value="wind">Wind</option>
                <option value="star">Star</option>
              </select>
            </div>
            <div className="flex items-end gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={cat.featured} onChange={e => onChange({ ...cat, featured: e.target.checked })} className="accent-[#D4AF37] w-4 h-4" />
                <span className="text-white/70 text-sm">Featured (large card)</span>
              </label>
            </div>
          </div>
          <BilingualField label="Category Title" valueEn={cat.title_en} valueAr={cat.title_ar} onChangeEn={v => onChange({ ...cat, title_en: v })} onChangeAr={v => onChange({ ...cat, title_ar: v })} />
          <BilingualField label="Badge / Collection Label" placeholder="e.g. Flagship Collection" valueEn={cat.badge_en || ''} valueAr={cat.badge_ar || ''} onChangeEn={v => onChange({ ...cat, badge_en: v })} onChangeAr={v => onChange({ ...cat, badge_ar: v })} />
          <BilingualField label="Tagline (popup)" placeholder="e.g. The pinnacle of tobacco craftsmanship" valueEn={cat.tagline_en || ''} valueAr={cat.tagline_ar || ''} onChangeEn={v => onChange({ ...cat, tagline_en: v })} onChangeAr={v => onChange({ ...cat, tagline_ar: v })} />
          <BilingualField label="Short Description (card)" type="textarea" valueEn={cat.description_en} valueAr={cat.description_ar} onChangeEn={v => onChange({ ...cat, description_en: v })} onChangeAr={v => onChange({ ...cat, description_ar: v })} />
          <BilingualField label="Extended Description (popup)" type="textarea" valueEn={cat.modal_content_en} valueAr={cat.modal_content_ar} onChangeEn={v => onChange({ ...cat, modal_content_en: v })} onChangeAr={v => onChange({ ...cat, modal_content_ar: v })} />
          <BilingualField label="Key Features — one per line (popup)" type="textarea" rows={5} placeholder={"Hand-selected tobacco leaves\nMinimum 24-month aging\nISO certified"} valueEn={cat.features_en || ''} valueAr={cat.features_ar || ''} onChangeEn={v => onChange({ ...cat, features_en: v })} onChangeAr={v => onChange({ ...cat, features_ar: v })} />
          <BilingualField label="Variants — one per line (popup)" type="textarea" rows={4} placeholder={"Crown Regular\nCrown Ultra Light\nCrown Menthol"} valueEn={cat.variants_en || ''} valueAr={cat.variants_ar || ''} onChangeEn={v => onChange({ ...cat, variants_en: v })} onChangeAr={v => onChange({ ...cat, variants_ar: v })} />
          <BilingualField label="Origins — one per line (popup)" type="textarea" rows={4} placeholder={"Zimbabwe\nBrazil\nTurkey"} valueEn={cat.origins_en || ''} valueAr={cat.origins_ar || ''} onChangeEn={v => onChange({ ...cat, origins_en: v })} onChangeAr={v => onChange({ ...cat, origins_ar: v })} />
          <ImagePicker label="Category Image" value={cat.image_url} onChange={v => onChange({ ...cat, image_url: v })} />
        </div>
      )}
    </div>
  );
};

const ProductsEditorPage = () => {
  const [categories, setCategories] = useState([]);
  const [translations, setTranslations] = useState({ en: {}, ar: {} });
  const [popupColumns, setPopupColumns] = useState(3);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminApi.get('/admin/content').then(r => {
      setCategories(r.data?.products?.categories || []);
      setTranslations(r.data?.translations || { en: {}, ar: {} });
      setPopupColumns(r.data?.products?.popup_columns || 3);
    }).catch(() => toast.error('Failed to load content'));
  }, []);

  const setTrans = (lang, key, value) => {
    setTranslations(prev => ({
      ...prev,
      [lang]: { ...prev[lang], products: { ...(prev[lang]?.products || {}), [key]: value } }
    }));
  };

  const moveCategory = (index, dir) => {
    const cats = [...categories];
    const swap = index + dir;
    if (swap < 0 || swap >= cats.length) return;
    [cats[index], cats[swap]] = [cats[swap], cats[index]];
    setCategories(cats.map((c, i) => ({ ...c, order: i + 1 })));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await Promise.all([
        adminApi.patch('/admin/content/products', {
          popup_columns: popupColumns,
          categories: categories.map((c, i) => ({ ...c, order: i + 1 })),
        }),
        adminApi.patch('/admin/content/translations', translations),
      ]);
      toast.success('Products saved');
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const en = translations.en?.products || {};
  const ar = translations.ar?.products || {};

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-white">Products Section</h1>
          <p className="text-white/40 text-sm mt-0.5">Manage product categories and section text</p>
        </div>
        <button onClick={handleSave} disabled={saving} className="flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-black font-semibold text-sm rounded-sm hover:bg-[#C5A028] transition-colors disabled:opacity-50">
          <Save className="w-4 h-4" />{saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      <Card title="Section Header">
        <BilingualField label="Overline" valueEn={en.overline || ''} valueAr={ar.overline || ''} onChangeEn={v => setTrans('en', 'overline', v)} onChangeAr={v => setTrans('ar', 'overline', v)} />
        <BilingualField label="Title" valueEn={en.title || ''} valueAr={ar.title || ''} onChangeEn={v => setTrans('en', 'title', v)} onChangeAr={v => setTrans('ar', 'title', v)} />
        <BilingualField label="Subtitle" valueEn={en.subtitle || ''} valueAr={ar.subtitle || ''} onChangeEn={v => setTrans('en', 'subtitle', v)} onChangeAr={v => setTrans('ar', 'subtitle', v)} />
        <BilingualField label="CTA Button Text" valueEn={en.cta || ''} valueAr={ar.cta || ''} onChangeEn={v => setTrans('en', 'cta', v)} onChangeAr={v => setTrans('ar', 'cta', v)} />
      </Card>

      <Card title="Popup Layout">
        <p className="text-white/40 text-xs -mt-1">Controls how many products appear per row when visitors click "Products" in the navigation.</p>
        <div className="flex gap-3 pt-1">
          {[2, 3].map(n => (
            <button
              key={n}
              onClick={() => setPopupColumns(n)}
              className={`flex-1 flex flex-col items-center gap-3 p-4 rounded-sm border transition-all duration-200 ${
                popupColumns === n
                  ? 'border-[#D4AF37] bg-[rgba(212,175,55,0.1)]'
                  : 'border-[rgba(212,175,55,0.15)] hover:border-[rgba(212,175,55,0.35)]'
              }`}
            >
              {/* Visual preview of column layout */}
              <div className={`w-full grid gap-1 ${n === 2 ? 'grid-cols-2' : 'grid-cols-3'}`}>
                {Array.from({ length: n * 2 }).map((_, i) => (
                  <div
                    key={i}
                    className={`h-8 rounded-[2px] transition-colors ${
                      popupColumns === n ? 'bg-[rgba(212,175,55,0.4)]' : 'bg-white/10'
                    }`}
                  />
                ))}
              </div>
              <span className={`text-sm font-semibold ${popupColumns === n ? 'text-[#D4AF37]' : 'text-white/50'}`}>
                {n} per row
              </span>
            </button>
          ))}
        </div>
      </Card>

      <Card title="Product Categories">
        <div className="space-y-3">
          {categories.map((cat, index) => (
            <CategoryCard
              key={cat.id}
              cat={cat}
              index={index}
              total={categories.length}
              onChange={updated => setCategories(cats => cats.map((c, i) => i === index ? updated : c))}
              onDelete={() => setCategories(cats => cats.filter((_, i) => i !== index))}
              onMoveUp={() => moveCategory(index, -1)}
              onMoveDown={() => moveCategory(index, 1)}
            />
          ))}
          <button onClick={() => setCategories(c => [...c, emptyCategory()])} className="flex items-center gap-2 text-[#D4AF37] text-xs hover:text-[#C5A028] transition-colors mt-2">
            <Plus className="w-3.5 h-3.5" />Add Category
          </button>
        </div>
      </Card>

      <Card title="Quality Banner">
        {['quality'].map(section => (
          <div key={section} className="space-y-4">
            <BilingualField label="Overline" valueEn={translations.en?.quality?.overline || ''} valueAr={translations.ar?.quality?.overline || ''} onChangeEn={v => setTranslations(prev => ({ ...prev, en: { ...prev.en, quality: { ...(prev.en?.quality || {}), overline: v } } }))} onChangeAr={v => setTranslations(prev => ({ ...prev, ar: { ...prev.ar, quality: { ...(prev.ar?.quality || {}), overline: v } } }))} />
            <BilingualField label="Title" valueEn={translations.en?.quality?.title || ''} valueAr={translations.ar?.quality?.title || ''} onChangeEn={v => setTranslations(prev => ({ ...prev, en: { ...prev.en, quality: { ...(prev.en?.quality || {}), title: v } } }))} onChangeAr={v => setTranslations(prev => ({ ...prev, ar: { ...prev.ar, quality: { ...(prev.ar?.quality || {}), title: v } } }))} />
            <BilingualField label="Description" type="textarea" valueEn={translations.en?.quality?.description || ''} valueAr={translations.ar?.quality?.description || ''} onChangeEn={v => setTranslations(prev => ({ ...prev, en: { ...prev.en, quality: { ...(prev.en?.quality || {}), description: v } } }))} onChangeAr={v => setTranslations(prev => ({ ...prev, ar: { ...prev.ar, quality: { ...(prev.ar?.quality || {}), description: v } } }))} />
          </div>
        ))}
      </Card>
    </div>
  );
};

export default ProductsEditorPage;
